import React, { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { FormSubentityInterface } from 'src/shared/types/entities/form-interface'
import { DragAndDropIcon } from 'src/editor/shared/icons/drag-and-drop-icon'

const FORM_SUBENTITY = 'form_subentity'

export interface FormSubentityDndItemProps {
  id: FormSubentityInterface['id']
  children: React.ReactNode
  index: number
  onMove: (dragIndex: number, hoverIndex: number) => void
}

interface DragItemInterface {
  index: number
  id: FormSubentityInterface['id']
  type: string
}

export const FormSubentityDndItem = ({
  id,
  children,
  index,
  onMove,
}: FormSubentityDndItemProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const isDraggable = useRef<boolean>(false)
  const [, drop] = useDrop<DragItemInterface, void, void>({
    accept: FORM_SUBENTITY,
    hover(item: DragItemInterface) {
      if (ref.current) {
        const dragIndex = item.index
        const hoverIndex = index

        // NOTE: Don't replace items with themselves
        if (dragIndex !== hoverIndex) {
          onMove(dragIndex, hoverIndex)

          /**
           * NOTE: we're mutating the monitor item here!
           * Generally it's better to avoid mutations,
           * but it's good here for the sake of performance
           * to avoid expensive index searches.
           */
          item.index = hoverIndex
        }
      }
    },
  })

  const [{ isDragging }, drag] = useDrag({
    item: { type: FORM_SUBENTITY, id, index },
    canDrag: () => isDraggable.current,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      style={{ opacity: isDragging ? 0 : 1 }}
      className="flex items-center gap-2 [&>div]:basis-full"
    >
      <DragAndDropIcon
        className="shrink-0 cursor-move"
        onMouseOver={() => {
          isDraggable.current = true
        }}
        onMouseOut={() => {
          isDraggable.current = false
        }}
      />
      {children}
    </div>
  )
}
